import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, Truck, PackageCheck, MapPin, ShoppingCart, MessageSquareText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicPO() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/retail/public/po/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Esta orden de compra no está disponible o el enlace no es válido."));
  }, [token]);

  if (err) return <Shell><div className="text-center text-navy/60 py-10" data-testid="po-public-error">{err}</div></Shell>;
  if (!data) return <Shell><div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet" /></div></Shell>;

  const q = data.computed || { items: [] };
  const items = (q.items || []).filter((it) => (it.kind || "item") !== "header");
  const logoUrl = data.has_logo ? `${API}/retail/public/po/${token}/logo` : null;
  const pdfUrl = `${API}/retail/public/po/${token}/pdf`;
  const DeliveryIcon = data.delivery_method === "recoleccion_proveedor" ? Truck : data.delivery_method === "entrega_direccion" ? MapPin : PackageCheck;

  return (
    <Shell>
      {logoUrl && <img src={logoUrl} alt="Logo" data-testid="po-public-logo" className="h-14 max-w-[220px] object-contain mb-5" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-navy/40 flex items-center gap-1.5"><ShoppingCart className="w-3.5 h-3.5" /> Orden de compra de</div>
          <h1 className="text-2xl font-medium text-navy" data-testid="po-public-issuer">{data.issuer?.name || "—"}</h1>
          <div className="text-sm text-navy/50">Proveedor: {data.proveedor || "—"}{data.folio ? ` · Folio ${data.folio}` : ""}</div>
        </div>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="po-public-pdf" className="btn btn-outline text-sm inline-flex items-center gap-1.5"><Download className="w-4 h-4" /> Descargar PDF</a>
      </div>

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mb-5 text-sm rounded-xl border border-navy/10 bg-navy/[0.02] p-4" data-testid="po-public-fields">
        <Field label="No. de orden" value={data.folio} />
        <Field label="Proveedor" value={data.proveedor} />
        <Field label="RFC" value={data.proveedor_rfc} />
        <Field label="Contacto" value={data.proveedor_contacto} />
        <Field label="Teléfono" value={data.proveedor_telefono} />
        <Field label="E-mail" value={data.proveedor_email} />
        <Field label="Dirección del proveedor" value={data.proveedor_direccion} />
        <Field label="Usuario solicitante" value={data.comprador} />
        <Field label="CEDIS de entrega" value={data.cedis} />
        <Field label="Fecha de elaboración" value={data.fecha} />
        <Field label="Ventana de entrega" value={data.fecha_entrega} />
        <Field label="Condiciones de pago" value={data.condiciones_pago} />
        <Field label="Moneda" value={data.moneda} />
        <Field label="Piezas totales" value={Number(data.piezas) > 0 ? String(data.piezas) : ""} />
        <Field label="Estado" value={data.estado_label} />
      </div>

      {data.delivery_label && (
        <div className="rounded-xl border border-navy/10 bg-navy/[0.02] p-3 mb-5 flex items-start gap-2.5 text-sm" data-testid="po-public-delivery">
          <DeliveryIcon className="w-4 h-4 text-violet mt-0.5" />
          <span className="text-navy/70">
            {data.delivery_label}
            {data.delivery_method === "entrega_direccion" && data.delivery_address ? <><br /><b>Dirección de entrega:</b> {data.delivery_address}</> : null}
            {data.delivery_note ? ` — ${data.delivery_note}` : ""}
          </span>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-navy/10">
        <table className="w-full text-sm" data-testid="po-public-items">
          <thead><tr className="bg-navy/[0.03] text-navy/50 text-xs"><th className="text-left font-medium p-3">Concepto</th><th className="text-right font-medium p-3">Cant.</th><th className="text-right font-medium p-3">P. unitario</th><th className="text-right font-medium p-3">Importe</th></tr></thead>
          <tbody>
            {items.map((it, i) => {
              const line = Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100);
              return (
                <tr key={i} className="border-t border-navy/5">
                  <td className="p-3 text-navy/80">{it.description}</td>
                  <td className="p-3 text-right text-navy/60 whitespace-nowrap">{it.qty}{it.unit ? <span className="text-navy/40"> {it.unit}</span> : null}</td>
                  <td className="p-3 text-right text-navy/60">{money(it.unit_price)}</td>
                  <td className="p-3 text-right text-navy/80">{money(line)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!q.hide_total && (
        <div className="mt-4 ml-auto max-w-xs space-y-1.5 text-sm" data-testid="po-public-totals">
          <div className="flex justify-between text-navy/60"><span>Subtotal</span><span>{money(q.subtotal)}</span></div>
          {Number(q.discount_amount) > 0 && <div className="flex justify-between text-navy/60"><span>Descuento</span><span>−{money(q.discount_amount)}</span></div>}
          {Number(q.credit_amount) > 0 && <div className="flex justify-between text-emerald-700" data-testid="po-public-credit"><span>Saldo a favor</span><span>−{money(q.credit_amount)}</span></div>}
          <div className="flex justify-between text-navy/60"><span>IVA {q.tax_included ? "(incl.)" : `(${q.tax_pct || 0}%)`}</span><span>{money(q.tax_amount)}</span></div>
          <div className="flex justify-between text-lg font-medium text-navy pt-1.5 border-t border-navy/10"><span>Total</span><span data-testid="po-public-total">{money(q.total)}</span></div>
          {data.total_letras && <div className="text-[11px] text-navy/50 text-right pt-0.5" data-testid="po-public-total-letras">({data.total_letras})</div>}
        </div>
      )}

      {data.comentarios && (
        <div className="mt-6 rounded-xl border border-navy/10 bg-navy/[0.02] p-4 text-sm" data-testid="po-public-comments">
          <div className="text-xs uppercase tracking-wide text-navy/40 mb-1.5 flex items-center gap-1.5"><MessageSquareText className="w-3.5 h-3.5" /> Comentarios</div>
          <p className="text-navy/75 whitespace-pre-wrap">{data.comentarios}</p>
        </div>
      )}
    </Shell>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-navy/5 py-1">
      <span className="text-navy/45">{label}</span>
      <span className="text-navy/80 text-right">{value}</span>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-cream py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl border-t-4 border-violet shadow-sm p-6 sm:p-8">{children}</div>
    </div>
  );
}
